import { map } from 'rxjs/operators';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { Routes } from '../../config/routes';
import { BillPayment } from '../../models/entities/bill-payment';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LobBankAccount } from '../../models/entities/lob-bank-account';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

/**
 * A Lob bank account's verification method and how many verification attempts
 * are left before Lob blocks it (SD-7089, SD-10048).
 */
export interface LobVerificationMethod
{
    microdepositType: string;
    attemptsRemaining: number | null;
    // False when the account predates core's attempt count, so attemptsRemaining
    // is only an upper bound (SD-10048, F-0454).
    attemptsRemainingIsExact: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class BillPayService
{
    public lobBankAccountChanged: EventEmitter<{}> = new EventEmitter<{}>();

    constructor(
            private _http: ApplicationHttpClient,
            private _dialog: MatDialog,
    ) {
    }

    /**
     * Get lob bank accounts for a member
     *
     * @param {number} organizationId
     * @returns {Observable<LobBankAccount[]>}
     */
    public getLobBankAccountsForOrganization(
            organizationId: number,
    ): Observable<LobBankAccount[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .get(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.BillPayBankAccounts() + '/' + organizationId,
                        ),
                ).pipe(
                        map((response: Object[]): LobBankAccount[] => {
                            return response.map(account => {
                                return new LobBankAccount(account);
                            });
                        }));
    }

    /**
     * @param lobBankAccountId
     */
    public refreshLobAccountVerification(lobBankAccountId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .post(Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.BillPayBankAccount(lobBankAccountId) + '/refresh',
                ), JSON.stringify({}));
    }

    /**
     * Get all payment history (with trashed) for an org
     *
     * @param {number} orgId
     * @param vendorId
     * @returns {Observable<LobBankAccount[]>}
     */
    public getPaymentHistory(
            orgId: number,
            vendorId?: number,
    ): Observable<BillPayment[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPaymentHistory(orgId || jwt.orgId))
                + (vendorId ? `?vendorId=${vendorId}` : '');

        return this._http.get(url).pipe(map((response: Object[]): BillPayment[] => {
            return response.map(payment => {
                return new BillPayment(payment);
            });
        }));
    }

    /**
     * delete a lob account
     * @param accountId
     */
    public deleteLobBankAccount(accountId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPayBankAccount(accountId));

        return this._http.delete(url);
    }

    /**
     * Save lob bank account for a member
     *
     * @param body
     * @returns {Observable<LobBankAccount>}
     */
    public createLobBankAccount(
            body: {
                description: string,
                routingNumber: string,
                accountNumber: string,
                signatory: string
            },
    ): Observable<LobBankAccount> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPayBankAccounts());

        return this._http.post(url, JSON.stringify(body)).pipe(map(
                (response: Object): LobBankAccount => {
                    return new LobBankAccount(response);
                },
        ));
    }

    public updateLobBankAccount(
            lobAccountId: number,
            body: {
                description: string,
                shareWithOrg: boolean,
                shareWithPmc?: boolean
            },
    ): Observable<LobBankAccount> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPayBankAccount(lobAccountId));

        return this._http.patch(url, JSON.stringify(body)).pipe(map(
                (response: Object): LobBankAccount => {
                    return new LobBankAccount(response);
                },
        ));
    }

    /**
     * Cancel a legacy bill payment
     * Note: This is only used for cancelling existing legacy payments created before the
     * bill pay system was deprecated. New payments should use the Payables system.
     *
     * @param {number} paymentId
     * @returns {Observable<Object>}
     */
    public cancelBillPayment(paymentId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPayCancel(jwt.orgId, paymentId));
        return this._http.delete(url);
    }

    /**
     * Read the Lob verification method (microdeposit_type) for an account so
     * the verification modal can render the correct entry field. Lob returns
     * either 'amounts' (two micro-deposit amounts) or 'descriptor_code' (a
     * 6-character statement descriptor code) (SD-7089). Also returns how many
     * verification attempts are left, so a reopened modal can keep warning
     * after a wrong code (SD-10048).
     *
     * @param {number} lobAccountId
     * @returns {Observable<LobVerificationMethod>}
     */
    public getLobVerificationMethod(lobAccountId: number): Observable<LobVerificationMethod> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .get(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.BillPayBankAccount(lobAccountId) + '/verification-method',
                        ),
                ).pipe(
                        map((response: LobVerificationMethod): LobVerificationMethod => {
                            return {
                                microdepositType: response.microdepositType,
                                attemptsRemaining: response.attemptsRemaining ?? null,
                                attemptsRemainingIsExact: response.attemptsRemainingIsExact === true,
                            };
                        }));
    }

    /**
     * Verify the lob account for a member
     *
     * Submits either the two micro-deposit amounts or the 6-character statement
     * descriptor code, depending on the account's Lob verification method
     * (SD-7089).
     *
     * @param {number} lobAccountId
     * @param body Either {amount1, amount2} or {descriptorCode}
     * @returns {Observable<LobBankAccount>}
     */
    public verifyLobAccount(
            lobAccountId: number,
            body: { amount1: number; amount2: number } | { descriptorCode: string },
    ): Observable<LobBankAccount> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .post(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.BillPayBankAccount(lobAccountId),
                        ),
                        JSON.stringify(body),
                ).pipe(
                        map(
                                (response: Object): LobBankAccount => {
                                    return new LobBankAccount(response);
                                },
                        ));
    }

    /**
     * Emit after confirming in microdeposit modal
     */
    public lobBankAccountChange() {
        this.lobBankAccountChanged.emit();
    }

    /**
     * Download file helper method (following Units pattern)
     * @param {string} url
     * @param {string} contentType
     * @returns {Observable<any>}
     */
    private downloadFile(url: string, contentType: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': contentType,
        });

        const options = {
            headers: headers,
            responseType: 'arraybuffer' as 'arraybuffer',
        };

        return this._http.get(url, options);
    }

    /**
     * Export payment history as CSV
     * @returns {Observable<any>}
     */
    public exportPaymentHistoryCsv(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
            `/organizations/${jwt.orgId}/payment-history/export?csv=1`
        );
        return this.downloadFile(url, 'text/csv');
    }

    /**
     * Export payment history as PDF
     * @returns {Observable<any>}
     */
    public exportPaymentHistoryPdf(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
            `/organizations/${jwt.orgId}/payment-history/export?pdf=1`
        );
        return this.downloadFile(url, 'application/pdf');
    }
}
